import React, { useState } from 'react';
import { Tv, WashingMachine, Utensils, Refrigerator, Wind } from 'lucide-react';
// This is a simplified implementation without the react-tabs library.
// In a real project, you might want to install and use that library.
const SoftServices = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = [{
    icon: <WashingMachine size={24} data-id="element-751" />,
    title: 'Washing Machine',
    content: <div className="space-y-6" data-id="element-752">
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-753">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-754">
              Washing machine won't turn on
            </h3>
            <p className="text-gray-700" data-id="element-755">
              First, check if the washing machine is receiving power. Use a
              multimeter to test the outlet voltage. If there's no power, check
              your home's electrical panel for any tripped circuit breakers.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-756">
              Sometimes the motor overheats and automatically shuts down to cool
              off. Wait a while and try again.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-757">
              Make sure the lid is fully closed. An open lid can prevent the
              machine from starting the wash cycle.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-758">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-759">
              Washing machine is not draining properly
            </h3>
            <p className="text-gray-700" data-id="element-760">
              A clogged drain hose can stop water from draining correctly.
              Remove the hose and flush it out with a garden hose to clear any
              blockages.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-761">
              Check the drain pump for jams. Remove and inspect it for foreign
              objects like socks, coins, or pens that might be stuck. These can
              block drainage.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-762">
              Using too much detergent causes excessive suds, which can slow or
              stop draining.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-763">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-764">
              Washing machine isn't spinning properly
            </h3>
            <p className="text-gray-700" data-id="element-765">
              Overloading the machine can cause it to become unbalanced and stop
              spinning. Try redistributing the clothes or reduce the load size.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-766">
              Check that all switches are working and correctly set. The spin
              speed selector should not be set between speeds.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-767">
              If the drain pump is blocked, you might hear a humming sound when
              water remains in the machine.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-768">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-769">
              Washing machine is leaking
            </h3>
            <p className="text-gray-700" data-id="element-770">Leaks may be caused by:</p>
            <ul className="list-disc pl-6 mt-2 text-gray-700" data-id="element-771">
              <li data-id="element-772">A clogged or cracked drain hose</li>
              <li data-id="element-773">A loose fill hose needing tightening</li>
              <li data-id="element-774">Rust or mineral build-up inside the water-inlet valve</li>
              <li data-id="element-775">
                Using too much detergent with hard water or water softeners
              </li>
              <li data-id="element-776">A faulty door seal on front-load machines</li>
              <li data-id="element-777">A failing drain pump seal</li>
            </ul>
          </div>
        </div>
  }, {
    icon: <Tv size={24} data-id="element-778" />,
    title: 'TV',
    content: <div className="space-y-6" data-id="element-779">
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-780">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-781">
              Incorrect Picture Size
            </h3>
            <p className="text-gray-700" data-id="element-782">
              If the TV image looks squashed, stretched, or cropped, it means
              the aspect ratio setting is not ideal. This setting is found in
              the TV menu and usually offers options like Zoom, Stretch, Wide,
              or 16:9 Aspect Ratio. Adjust it to the option that looks best to
              you.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-783">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-784">
              Inaccurate Colour
            </h3>
            <p className="text-gray-700" data-id="element-785">
              TVs are often preset with a 'white balance' that makes whites
              appear slightly bluer to create a cooler picture. This applies to
              Normal, Standard, and Vivid picture modes. You can adjust this by
              finding the White Balance or Colour Temperature settings in the TV
              menu and choosing between Cool, Normal, or Warm according to your
              preference.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-786">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-787">
              Turning Captions On or Off
            </h3>
            <p className="text-gray-700" data-id="element-788">
              You can toggle programme captions using the Closed Caption button
              on your TV remote. Captions can also be turned on or off via the
              TV's settings menu.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-789">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-790">
              Audio Delay
            </h3>
            <p className="text-gray-700" data-id="element-791">
              An audio delay happens when the picture and sound are out of sync,
              causing a 'voice-over' effect where you see the action before
              hearing it. To fix this, adjust the audio delay setting in the
              TV's audio menu.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-792">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-793">
              TV Turns On but No Picture
            </h3>
            <p className="text-gray-700" data-id="element-794">
              This usually means the TV is set to the wrong input. Use the
              remote's 'Input,' 'Source,' or sometimes 'TV/Video' button to
              cycle through inputs until you see the picture. If the correct
              input is selected but there's still no picture, check that your
              cable or satellite box is on and properly connected. Resetting the
              cable box by unplugging and plugging it back in can also help.
            </p>
          </div>
        </div>
  }, {
    icon: <Utensils size={24} data-id="element-795" />,
    title: 'Cooker/Oven',
    content: <div className="space-y-6" data-id="element-796">
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-797">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-798">
              Cooker does not turn on
            </h3>
            <p className="text-gray-700" data-id="element-799">
              This usually means the cooker isn't receiving power. Check your
              home's electrical panel to see if a circuit breaker has tripped.
              If it has, reset it and try again.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-800">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-801">
              Cooker elements do not heat up
            </h3>
            <p className="text-gray-700" data-id="element-802">Several issues could cause this:</p>
            <ul className="list-disc pl-6 mt-2 text-gray-700" data-id="element-803">
              <li data-id="element-804">
                The thermostat may have tripped. Solution: Turn off the oven and
                wait for it to fully cool before trying again.
              </li>
              <li data-id="element-805">
                The timer might be set to Auto. Solution: Switch the timer to
                Manual mode.
              </li>
              <li data-id="element-806">
                The clock time might not be set. Solution: Ensure the clock is
                set correctly.
              </li>
              <li data-id="element-807">
                The heating element could be faulty. Solution: Replace the
                faulty element.
              </li>
            </ul>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-808">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-809">
              Oven does not reach the correct temperature
            </h3>
            <p className="text-gray-700" data-id="element-810">
              A likely cause is a faulty temperature sensor inside the oven.
              This sensor measures the heat to prevent overheating.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-811">
              Check that the sensor isn't touching the oven wall, as this can
              cause inaccurate temperature readings. If faulty, the sensor
              should be replaced.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-812">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-813">
              Oven cooks food unevenly
            </h3>
            <p className="text-gray-700" data-id="element-814">
              Uneven cooking could be due to problems with the heating elements
              or the temperature sensor.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-815">
              Preheat the oven and visually check if the heating elements glow
              or light up. If they work fine, the temperature sensor might be
              the issue. Replace any faulty parts.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-816">
              Also, try adjusting oven racks or using different cookware, which
              can sometimes improve cooking results.
            </p>
          </div>
        </div>
  }, {
    icon: <Refrigerator size={24} data-id="element-817" />,
    title: 'Fridge/Freezer',
    content: <div className="space-y-6" data-id="element-818">
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-819">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-820">
              Fridge Is Too Warm or Too Cold
            </h3>
            <p className="text-gray-700" data-id="element-821">
              If your fridge is not maintaining the right temperature, the issue
              could be with the air damper, baffle, or diffuser. These
              components regulate the flow of cold air from the evaporator fan
              into the fresh food compartment. A damaged baffle can cause the
              fridge to be too warm or too cold.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-822">
              The baffle is typically located where the cold air enters the
              fridge's fresh food section. It is usually encased in plastic with
              Styrofoam to prevent air leaks.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-823">
              Check that air is flowing freely and nothing is blocking the
              airflow. Some fridges have a temperature sensing bulb to help
              adjust the interior temperature. If this bulb is misplaced or
              damaged, it should be repositioned or replaced to restore normal
              cooling.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-824">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-825">
              Fridge Is Leaking
            </h3>
            <p className="text-gray-700" data-id="element-826">
              If you notice water leaking from your fridge, the door gasket or
              seal might be faulty. The gasket is made of vinyl with a flexible
              magnetic strip that creates an airtight seal around the door.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-827">
              Moisture around the door edge usually means the gasket is damaged.
              Even a small air leak can cause water buildup or defrosting
              problems.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-828">
              Tip: Before installing a new gasket, warm it slightly in a tumble
              dryer to make it more flexible and easier to fit.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-829">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-830">
              Fridge Is Noisy
            </h3>
            <p className="text-gray-700" data-id="element-831">
              A noisy fridge often points to an issue with the condenser fan.
              This fan circulates air through the condenser coil to remove heat
              and over the drain pan to evaporate water.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-832">
              To check, disconnect power and remove the rear panel. Make sure
              nothing is blocking the airflow. Inspect the fan motor and blades
              for damage or wear, and replace parts if needed.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-833">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-834">
              Frost Builds Up in Freezer
            </h3>
            <p className="text-gray-700" data-id="element-835">
              Frost in the freezer forms when warm, moist air enters the unit as
              the door opens and closes. A small amount of frost is normal, but
              excessive frost can cause damage.
            </p>
            <p className="text-gray-700 mt-2" data-id="element-836">
              Too much frost often means the freezer door or gasket is damaged,
              letting warm air in when the door is shut. Other causes could be a
              faulty defrost sensor, heating element, or defrost timer that
              aren't working properly.
            </p>
          </div>
        </div>
  }, {
    icon: <Wind size={24} data-id="element-837" />,
    title: 'Tumble Dryer',
    content: <div className="space-y-6" data-id="element-838">
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-839">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-840">
              Tumble Dryer Does Not Turn On
            </h3>
            <p className="text-gray-700" data-id="element-841">
              If your tumble dryer won't start, first check if it's receiving
              power. Use a multi-meter to test the outlet voltage. If there's no
              power, inspect your home's electrical panel for any tripped
              circuit breakers. Also, ensure the dryer door is fully closed, as
              an open door can prevent operation. Additionally, the motor may
              have overheated and shut down temporarily; wait for it to cool
              before trying again.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-842">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-843">
              Tumble Dryer Not Draining Properly
            </h3>
            <p className="text-gray-700" data-id="element-844">
              A clogged drain hose can stop your dryer from draining correctly.
              Remove the hose and flush it with a garden hose to clear any
              blockages. Check the drain pump for jams—foreign objects like
              coins, socks, or pens may be stuck inside, blocking drainage.
              Excessive detergent use can also cause too many suds, delaying
              draining.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-845">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-846">
              Tumble Dryer Not Spinning Properly
            </h3>
            <p className="text-gray-700" data-id="element-847">
              Overloading the dryer can unbalance the load and prevent it from
              spinning. Try redistributing the clothes or reducing the load
              size. Faulty switches or incorrectly set speed selectors can also
              cause spinning issues. If you hear a humming noise when the dryer
              is full of water, there may be an object stuck in the drain pump.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-848">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-849">
              Tumble Dryer Is Leaking
            </h3>
            <p className="text-gray-700" data-id="element-850">
              Leaks can be caused by a clogged or cracked drain hose, a loose
              fill hose, or leaks from the water-inlet valve due to rust or
              mineral buildup. Using too much detergent with hard water or water
              softeners can also lead to leaks. Faulty door seals or drain pump
              seals may cause water to seep out and might need replacing.
            </p>
          </div>
          <div className="bg-mwc-lightpink p-6 rounded-lg" data-id="element-851">
            <h3 className="text-lg font-semibold text-mwc-burgundy mb-3" data-id="element-852">
              Tumble Dryer Runs But Does Not Heat
            </h3>
            <p className="text-gray-700" data-id="element-853">
              Blocked ventilation, a clogged lint screen, or consistent
              overloading can cause the thermal fuse to fail. If the fuse is
              broken, it will need to be replaced.
            </p>
          </div>
        </div>
  }];
  return <div className="min-h-screen bg-white" data-id="element-854">
      <div className="bg-mwc-burgundy text-white py-12" data-id="element-855">
        <div className="container mx-auto px-4" data-id="element-856">
          <div className="flex flex-col md:flex-row items-center" data-id="element-857">
            <div className="md:w-2/3" data-id="element-858">
              <h1 className="text-3xl font-bold mb-4" data-id="element-859">
                Soft Services and Guides
              </h1>
              <p className="text-lg max-w-3xl" data-id="element-860">
                Welcome to our Soft Services and Guides page, your go-to
                resource for appliance maintenance advice and practical
                troubleshooting tips. At Maplewood-Care, we don't just fix
                appliances; we help you keep them in great shape through expert
                guidance and preventative care.
              </p>
            </div>
            <div className="md:w-1/3 flex justify-center mt-8 md:mt-0" data-id="element-861">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vmVW7MegZujKRFwVEK2D8B/soft_services.png" alt="Maplewood Care Mascot Reading" className="max-w-full md:max-w-xs object-contain" data-id="element-862" />
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-12" data-id="element-863">
        <div className="mb-12" data-id="element-864">
          <p className="text-gray-700 mb-6" data-id="element-865">
            Here, you'll find easy-to-understand guides designed to help you:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-866">
            <li className="bg-mwc-lightpink p-4 rounded-lg flex items-center" data-id="element-867">
              <div className="bg-white rounded-full p-2 mr-3 text-mwc-burgundy" data-id="element-868">
                <span className="font-bold" data-id="element-869">1</span>
              </div>
              <span className="text-gray-700" data-id="element-870">
                Identify common issues with your appliances
              </span>
            </li>
            <li className="bg-mwc-lightpink p-4 rounded-lg flex items-center" data-id="element-871">
              <div className="bg-white rounded-full p-2 mr-3 text-mwc-burgundy" data-id="element-872">
                <span className="font-bold" data-id="element-873">2</span>
              </div>
              <span className="text-gray-700" data-id="element-874">
                Perform basic troubleshooting safely at home
              </span>
            </li>
            <li className="bg-mwc-lightpink p-4 rounded-lg flex items-center" data-id="element-875">
              <div className="bg-white rounded-full p-2 mr-3 text-mwc-burgundy" data-id="element-876">
                <span className="font-bold" data-id="element-877">3</span>
              </div>
              <span className="text-gray-700" data-id="element-878">
                Know when to call a professional for repairs
              </span>
            </li>
            <li className="bg-mwc-lightpink p-4 rounded-lg flex items-center" data-id="element-879">
              <div className="bg-white rounded-full p-2 mr-3 text-mwc-burgundy" data-id="element-880">
                <span className="font-bold" data-id="element-881">4</span>
              </div>
              <span className="text-gray-700" data-id="element-882">
                Maintain your appliances to extend their lifespan
              </span>
            </li>
          </ul>
          <p className="text-gray-700 mt-6" data-id="element-883">
            Our soft services include regular maintenance plans that help
            prevent breakdowns and keep your home running smoothly. These guides
            are part of our commitment to providing value beyond repairs,
            empowering you with the knowledge to look after your essential
            household appliances.
          </p>
          <p className="text-gray-700 mt-4" data-id="element-884">
            If you can't find the solution you're looking for or need
            professional assistance, don't hesitate to contact our expert team.
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-885">
          <div className="flex overflow-x-auto border-b" data-id="element-886">
            {tabs.map((tab, index) => <button key={index} className={`px-6 py-4 flex items-center whitespace-nowrap font-medium ${activeTab === index ? 'text-mwc-burgundy border-b-2 border-mwc-burgundy' : 'text-gray-600 hover:text-mwc-burgundy'}`} onClick={() => setActiveTab(index)} data-id="element-887">
                <span className="mr-2" data-id="element-888">{tab.icon}</span>
                {tab.title}
              </button>)}
          </div>
          <div className="p-6" data-id="element-889">{tabs[activeTab].content}</div>
        </div>
        <div className="mt-12 bg-mwc-lightpink p-6 rounded-lg" data-id="element-890">
          <h2 className="text-xl font-bold text-mwc-burgundy mb-4" data-id="element-891">
            Need Professional Help?
          </h2>
          <p className="text-gray-700 mb-6" data-id="element-892">
            If you've tried these troubleshooting steps and still have issues,
            our expert engineers are ready to help. Contact us to arrange a
            convenient repair appointment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-893">
            <a href="tel:08000554969" className="bg-mwc-burgundy hover:bg-mwc-darkburgundy text-white font-semibold px-6 py-3 rounded-md transition-colors duration-300 text-center" data-id="element-894">
              Call 0800 055 4969
            </a>
            <a href="mailto:support@maplewood-care.com" className="bg-mwc-green hover:bg-mwc-green/80 text-white font-semibold px-6 py-3 rounded-md transition-colors duration-300 text-center" data-id="element-895">
              Email Our Support Team
            </a>
          </div>
        </div>
      </div>
    </div>;
};
export default SoftServices;